/* eslint-disable react/sort-prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useReducer, useState, useEffect } from 'react';
import {
  Button,
  Form,
  FormLayout,
  Link,
  TextField,
  Card,
} from '@shopify/polaris';
import PropTypes from 'prop-types';
import styleConstants from 'src/styles/constants';
import { useMutation } from '@apollo/react-hooks';
import { TERMS_AND_CONDITIONS_URL, PRIVACY_POLICY_URL } from './constants';
import { validateFields } from '../../utils/validations';
import validations from './validations';
import SignUpMutation from '../../mutations/SignUpMutation';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

const reducer = (state, action) => {
  const { key, value } = action;
  return {
    ...state,
    [key]: value,
  };
};

const SignUpFragment = ({ onError, siteOwner, shop, onSuccess }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    email: siteOwner.email,
  });
  const { firstName, lastName, email, password } = state;
  const [signUp, { loading }] = useMutation(SignUpMutation);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitErrors = validateFields(state, validations);
    if (Object.keys(submitErrors).length) {
      return setValidationErrors(submitErrors);
    }

    try {
      const { data } = await signUp({
        variables: {
          ...state,
          shop,
        },
      });
      if (data && !data.signUp.result.success) {
        const { errors } = data.signUp.result;
        if (errors.length > 1) {
          return onError({
            title: 'Sign in unsuccessful.',
            message: errors.map(({ message }) => message),
          });
        }
        return onError(errors[0]);
      }
      const { tenant, ...session } = data.signUp.session;
      return onSuccess(session, tenant);
    } catch (err) {
      return onError({
        title: 'Sign up unsuccessful.',
        message: err.message,
      });
    }
  };

  useEffect(() => {
    if (siteOwner) {
      dispatch({ key: 'email', value: siteOwner.email });
    }
  }, [siteOwner]);

  return (
    <Card>
      <Card.Section>
        <Form onSubmit={handleSubmit}>
          <FormLayout>
            <TextField
              error={
                validationErrors.firstName && (
                  <span>{validationErrors.firstName.required}</span>
                )
              }
              label="First Name"
              value={firstName}
              onChange={(value) => dispatch({ key: 'firstName', value })}
            />
            <TextField
              error={
                validationErrors.lastName && (
                  <span>{validationErrors.lastName.required}</span>
                )
              }
              label="Last Name"
              value={lastName}
              onChange={(value) => dispatch({ key: 'lastName', value })}
            />
            <TextField
              error={
                validationErrors.email && (
                  <>
                    {validationErrors.email.required && (
                      <span>{validationErrors.email.required}</span>
                    )}
                    {!validationErrors.email.required &&
                      validationErrors.email.email && (
                        <span>{validationErrors.email.email}</span>
                      )}
                  </>
                )
              }
              helpText="Will be used as the ‘site owner’ email"
              label="Email"
              type="email"
              value={email}
              onChange={(value) => dispatch({ key: 'email', value })}
            />
            <TextField
              error={
                validationErrors.password && (
                  <>
                    {validationErrors.password.required && (
                      <span>{validationErrors.password.required}</span>
                    )}
                    {!validationErrors.password.required &&
                      validationErrors.password.strength && (
                        <span>{validationErrors.password.strength}</span>
                      )}
                  </>
                )
              }
              label="Create Password"
              type="password"
              value={password}
              onChange={(value) => dispatch({ key: 'password', value })}
            />
          </FormLayout>
          <div className="signup__actions">
            <p className="Polaris-TextStyle--variationSubdued">
              By clicking this button, I agree to the Thinkific&nbsp;
              <Link url={TERMS_AND_CONDITIONS_URL} external>
                Terms and Conditions
              </Link>
              and
              <Link url={PRIVACY_POLICY_URL} external>
                &nbsp;Privacy Policy
              </Link>
            </p>
            <Button loading={loading} primary submit>
              Sign up for Thinkific
            </Button>
          </div>
        </Form>
        <style jsx>{`
          .signup__actions {
            display: flex;
            flex-direction: column;
            padding: ${styleConstants.spacing.five} 0
              ${styleConstants.spacing.four};
          }
          p {
            margin-bottom: ${styleConstants.spacing.five};
          }
        `}</style>
      </Card.Section>
    </Card>
  );
};

SignUpFragment.propTypes = {
  onError: PropTypes.func.isRequired,
  siteOwner: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  shop: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default SignUpFragment;
